import triggerEcommerceTracking from "../../common/functions/triggerEcommerceTracking";
import {
  set_related_story_list_slider,
} from "../../common/functions/others";
import setMediumListSliders from "../../common/functions/setMediumListSliders";
import set_related_articles_slider from "../../common/pages/users";
import { readMore } from "../../common/functions/searches";

const initializeArticleRelatedContents = () => {
  const relatedArticlesComponents =
    document.querySelectorAll(".jsRelatedArticles");
  const usersArticlesComponents = document.querySelectorAll(".jsUsersArticles");
  const relatedStoriesComponents =
    document.querySelectorAll("#jsRelatedStories");

  if (
    relatedArticlesComponents.length === 0 &&
    usersArticlesComponents.length === 0 &&
    relatedStoriesComponents.length === 0
  ) {
    return Promise.resolve();
  }

  // NOTE: 関連記事ABテスト用の対応として、ターゲット要素の class 切り替えを監視して表示を再評価する
  initializeRelatedArticlesExperimentObserver();

  return fetchRelatedContents(relatedArticlesComponents, usersArticlesComponents, relatedStoriesComponents)
    .then(data => {
      buildRelatedArticlesComponentsFromResponse(data, relatedArticlesComponents);
      buildUsersArticlesComponentsFromResponse(data, usersArticlesComponents);
      buildRelatedStoriesComponentsFromResponse(data, relatedStoriesComponents);
      handleCompletionTasks();
    })
    .catch((error) => {
      console.error("Related contents request failed:", error);
      throw error;
    });
};

const refreshRelatedArticlesExperimentViews = () => {
  setMediumListSliders();

  if (
    window.lazySizes &&
    window.lazySizes.loader &&
    typeof window.lazySizes.loader.checkElems === 'function'
  ) {
    window.lazySizes.loader.checkElems();
  }
};
const isVolunteerRelatedArticlesExperiment = articleType => articleType === 'volunteer';

const isClassBActiveForArticleType = (articleType, experimentTargetElement) => (
  isVolunteerRelatedArticlesExperiment(articleType) &&
  experimentTargetElement &&
  experimentTargetElement.classList.contains('optx-related-articles-b')
);


const initializeRelatedArticlesExperimentObserver = () => {
  if (!document.querySelector('.jsRelatedArticlesExperiment')) {
    return;
  }

  const observeClassMutation = (element, initializedKey) => {
    if (!element || element.dataset[initializedKey] === 'true') {
      return;
    }

    const observer = new MutationObserver(mutations => {
      if (!mutations.some(mutation => mutation.attributeName === 'class')) {
        return;
      }

      window.requestAnimationFrame(() => {
        refreshRelatedArticlesExperimentViews();
      });
    });

    observer.observe(element, {
      attributes: true,
      attributeFilter: ['class']
    });

    element.dataset[initializedKey] = 'true';
  };

  document.querySelectorAll('.jsRelatedArticlesExperimentTarget').forEach((element) => {
    observeClassMutation(element, 'relatedArticlesExperimentTargetObserverInitialized');
  });
};

const fetchRelatedContents = (relatedArticlesComponents, usersArticlesComponents, relatedStoriesComponents) => {
  const requests = [];
  const requestKeys = new Set();

  Array.from(relatedArticlesComponents).forEach(component => {
    const dataset = component.dataset;
    const request = {
      type: 'related_articles',
      key: dataset.articleType,
      params: {
        articleType: dataset.articleType,
        renderType: dataset.renderType,
        userId: dataset.userId,
        articleId: dataset.articleId,
        border_none: dataset.borderNone,
      }
    };
    const requestKey = JSON.stringify(request);
    if (requestKeys.has(requestKey)) return;

    requestKeys.add(requestKey);
    requests.push(request);
  });

  Array.from(usersArticlesComponents).forEach((component) => {
    const { dataset } = component;
    requests.push({
      type: "users_articles",
      key: `${dataset.articleType}_articles`,
      params: {
        userId: dataset.userId,
        articleType: dataset.articleType,
        excludedArticleId: dataset.excludedArticleId,
      },
    });
  });

  Array.from(relatedStoriesComponents).forEach((component) => {
    const { dataset } = component;
    requests.push({
      type: "related_stories",
      key: "related_stories",
      params: {
        articleId: dataset.articleId,
        storyId: dataset.storyId,
        searchParams: dataset.searchParams,
        numberOfStories: dataset.numberOfStories || 6,
        renderType: dataset.renderType || "medium",
        linkToStoriesIndex: dataset.linkToStoriesIndex,
        isTitleOutside: dataset.isTitleOutside,
        limit: dataset.limit || 3,
      },
    });
  });

  const articleId =
    (relatedArticlesComponents[0] &&
      relatedArticlesComponents[0].dataset.articleId) ||
    (usersArticlesComponents[0] &&
      usersArticlesComponents[0].dataset.excludedArticleId) ||
    (relatedStoriesComponents[0] &&
      relatedStoriesComponents[0].dataset.articleId);

  const params = new URLSearchParams();
  if (articleId) params.append("articleId", articleId);
  params.append("requests", JSON.stringify(requests));

  return fetch(`/async_info/articles/related_contents?${params.toString()}`, {
    method: "GET",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
    },
  }).then((response) => {
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    return response.json();
  });
};

const buildRelatedArticlesComponentsFromResponse = (data, components) => {
  if (!data.related_articles) return;

  components.forEach((component) => {
    const { articleType } = component.dataset;
    const responseData = data.related_articles[articleType];
    if (!responseData) return;

    if (responseData.error) {
      handleRelatedArticlesComponentError(component, articleType, responseData.error);
      return;
    }

    const classKey = component.dataset.classKey;
    const selectedResponseData = buildSelectedRelatedArticlesResponseData(responseData, classKey);

    if (selectedResponseData) {
      buildRelatedArticleComponent(component, selectedResponseData, articleType);
      return;
    }

    handleRelatedArticlesComponentError(component, articleType, 'Missing related articles response data');
  });
};

const buildRelatedArticleComponent = (component, responseData, articleType) => {
  if (responseData.error) {
    console.error(`Error loading ${articleType}:`, responseData.error);
    return;
  }

  applyRelatedArticlesExperimentState(component, responseData);
  component.dataset.empty = responseData.article_size === 0 ? 'true' : 'false';

  if (responseData.article_size === 0) {
    updateRelatedArticlesExperimentVisibility(component);
    return;
  }

  component.innerHTML = responseData.html;
  updateRelatedArticlesExperimentVisibility(component);

  if (
    responseData.excluded_article_ids &&
    responseData.excluded_article_ids.length > 0
  ) {
    removeExcludedArticles(responseData.excluded_article_ids);
  }

  if (component.offsetParent !== null) {
    // NOTE: gtmでのトラッキング用
    window.dispatchEvent(new CustomEvent('gtm_article_algorithm_rendering', {
      detail: { wrapperSelector: `#${component.id}` }
    }));

    // NOTE: eコマーストラッキング用
    triggerEcommerceTracking(component.id, 'person');
  }
};

const applyRelatedArticlesExperimentState = (component, responseData) => {
  const { classKey, articleType } = component.dataset;
  if (!classKey) return;

  const experimentElement = component.closest('.jsRelatedArticlesExperiment');
  if (!experimentElement) return;

  const fallback = !!(
    responseData &&
    responseData.recommendation_meta &&
    responseData.recommendation_meta.fallback === true
  );
  component.dataset.fallback = fallback ? 'true' : 'false';
  experimentElement.classList.toggle('isVolunteerExperiment', isVolunteerRelatedArticlesExperiment(articleType));

  if (classKey === 'class_b') {
    experimentElement.classList.toggle('isVertexFallback', fallback);
  }
};

const updateRelatedArticlesExperimentVisibility = component => {
  const experimentElement = component.closest('.jsRelatedArticlesExperiment');
  const experimentTargetElement = component.closest('.jsRelatedArticlesExperimentTarget');
  if (!experimentElement) {
    if (component.dataset.empty === 'true') {
      const parentElement = component.parentElement;
      if (parentElement) {
        parentElement.remove();
      }
    }
    return;
  }

  const classAComponent = experimentElement.querySelector('.jsRelatedArticles[data-class-key="class_a"]');
  const classBComponent = experimentElement.querySelector('.jsRelatedArticles[data-class-key="class_b"]');

  const classAEmpty = classAComponent ? classAComponent.dataset.empty === 'true' : true;
  const classBFallback = classBComponent ? classBComponent.dataset.fallback === 'true' : false;
  const classBEmpty = classBComponent ? classBComponent.dataset.empty === 'true' : true;
  const currentClassKey = component.dataset.classKey;
  const articleType = (classAComponent && classAComponent.dataset.articleType) ||
    (classBComponent && classBComponent.dataset.articleType);
  const isClassBVisible = isClassBActiveForArticleType(articleType, experimentTargetElement) && !classBFallback;

  // B側が表示対象のときは、B側の描画が終わるまでA側の空判定で枠を消さない。
  if (isClassBVisible && currentClassKey === 'class_a') {
    return;
  }

  const activeVariantEmpty = isClassBVisible ? classBEmpty : classAEmpty;

  if (activeVariantEmpty) {
    experimentElement.remove();
  }
};

const buildSelectedRelatedArticlesResponseData = (responseData, classKey) => {
  if (!classKey) return responseData;

  if (responseData[classKey]) return responseData[classKey];

  // NOTE: class_a / class_b 導入前の旧構造レスポンスも class_a として扱えるようにする
  if (classKey === 'class_a' && isLegacyRelatedArticlesResponse(responseData)) {
    return responseData;
  }

  return null;
};

const isLegacyRelatedArticlesResponse = responseData => (
  responseData &&
  typeof responseData.article_size !== 'undefined' &&
  typeof responseData.html !== 'undefined'
);

const handleRelatedArticlesComponentError = (component, articleType, error) => {
  console.error(`Error loading ${articleType}:`, error);

  const experimentElement = component.closest('.jsRelatedArticlesExperiment');
  if (experimentElement) {
    experimentElement.remove();
    return;
  }

  const parentElement = component.parentElement;
  if (parentElement) {
    parentElement.remove();
  }
};

const buildUsersArticlesComponentsFromResponse = (data, components) => {
  if (!data.users_articles) return;

  components.forEach((component) => {
    const { articleType } = component.dataset;
    const key = `${articleType}_articles`;
    const responseData = data.users_articles[key];

    if (responseData) {
      buildUsersArticleComponent(component, responseData, articleType);
    }
  });
};

const buildUsersArticleComponent = (component, responseData, articleType) => {
  if (responseData.error) {
    console.error(
      `Error loading users ${articleType} articles:`,
      responseData.error
    );
    return;
  }

  if (responseData.article_size === 0) {
    const { parentElement } = component;
    if (parentElement) {
      parentElement.remove();
    }
    return;
  }

  component.outerHTML = responseData.html;

  const existsElement = document.getElementById("jsUsersArticlesExists");
  if (existsElement) {
    existsElement.style.display = "block";
  }

  if (responseData.excluded_article_id) {
    removeExcludedUserArticle(responseData.excluded_article_id);
  }

  const listType = articleType === "job" ? "Job" : "Volunteer";
  const listId = `gtmUsersArticles${listType}`;
  const newElement = document.getElementById(listId);

  if (newElement) {
    triggerEcommerceTracking(listId);
  }
};

const removeExcludedArticles = (excludedArticleIds) => {
  excludedArticleIds.forEach((articleId) => {
    const articlesToRemove = document.querySelectorAll(
      `.jsArticle${articleId}`
    );
    articlesToRemove.forEach((article) => article.remove());
  });
};

const removeExcludedUserArticle = (excludedArticleId) => {
  const articlesToRemove = document.querySelectorAll(
    `.jsUserArticle${excludedArticleId}`
  );
  articlesToRemove.forEach((article) => article.remove());
};

const buildRelatedStoriesComponentsFromResponse = (data, components) => {
  if (!data.related_stories) return;

  components.forEach((component) => {
    const responseData = data.related_stories.related_stories;

    if (responseData) {
      buildRelatedStoryComponent(component, responseData);
    }
  });
};

const hydrateRelatedStoryThumbnails = container => {
  const thumbnails = container.querySelectorAll('.storyThumbnail[data-bg]');

  thumbnails.forEach(thumbnail => {
    const bg = thumbnail.getAttribute('data-bg');
    if (!bg) return;

    thumbnail.style.backgroundImage = `url(${JSON.stringify(bg)})`;
    thumbnail.classList.remove('jsLazy', 'lazyloading');
    thumbnail.classList.add('lazyloaded');
  });
};

const buildRelatedStoryComponent = (component, responseData) => {
  if (responseData.error) {
    console.error("Error loading related stories:", responseData.error);
    return;
  }

  if (responseData.html) {
    const parentElement = component.parentElement;
    component.outerHTML = responseData.html;
    const relatedStoriesContainer = parentElement && parentElement.querySelector('.m_storyContentRelated.stories');

    if (relatedStoriesContainer) {
      hydrateRelatedStoryThumbnails(relatedStoriesContainer);
    }

    set_related_story_list_slider();
    if (
      window.lazySizes &&
      window.lazySizes.loader &&
      typeof window.lazySizes.loader.checkElems === 'function'
    ) {
      window.requestAnimationFrame(() => {
        window.lazySizes.loader.checkElems();
      });
    }
  }
};

const handleCompletionTasks = () => {
  refreshRelatedArticlesExperimentViews();
  if (document.querySelector('.users.show.pc')) {
    set_related_articles_slider();
  }

  const adjustHeightElements = document.querySelectorAll(".jsAjustHeight");
  adjustHeightElements.forEach((element) => {
    element.style.minHeight = "auto";
  });

  readMore();
};

// NOTE: esbuild はモジュールレベルの副作用がない純粋な関数宣言をバンドルから除外する
// Sprockets からの移行のため、グローバルアクセスを維持するために window に公開する

export default initializeArticleRelatedContents;